import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "../../../hooks/useForm";
import PageWrapper from "../../common/components/PageWrapper";
import OmniTable from "../../omni-common/components/OmniTable";
import DraftSearchFilters from "../components/DraftSearchFilters";
import DraftTable, { Draft } from "../components/DraftTable";
import DraftBulkActions from "../components/DraftBulkActions";
import DraftDetailPage from "../components/DraftDetailPage";
import { useOmniDraftsQuery, useOmniDraftQuery, useOmniDeleteDraftMutation, DraftStatus, OmniDraftQuery, SearchFieldType, SortOrder } from "../../../lib/apollo/graphql/omni/omni-generated";

type DraftDetail = OmniDraftQuery['draft'];

interface DateRange {
  startDate?: string;
  endDate?: string;
}

export default function OmniDraftContainer() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [filters, onChange] = useForm({
    page: 1,
    limit: 10,
    searchQuery: "",
    searchType: "ALL",
    statusFilter: "",
    dateTypeFilter: "CREATED",
    sortKey: "",
    sortDirection: "" as "" | "ASC" | "DESC",
  });

  const [dateRange, setDateRange] = useState<DateRange>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedDatePreset, setSelectedDatePreset] = useState<string>("ALL");

  // 검색 필드 설정 함수
  const getSearchFields = (searchType: string): SearchFieldType[] | undefined => {
    switch (searchType) {
      case "TITLE":
        return [SearchFieldType.Title];
      case "CONTENT_TAG":
        // 내용+태그는 내용으로 검색하고 태그는 별도 필터 사용
        return [SearchFieldType.Content];
      case "TAG":
        // TAG인 경우 tags 필터로 처리하므로 undefined 반환
        return undefined;
      case "ALL":
        // ALL인 경우 모든 필드 검색
        return [SearchFieldType.Title, SearchFieldType.Content];
      default:
        return undefined;
    }
  };

  // 태그 검색과 텍스트 검색 구분
  const isTagSearch = filters.searchType === "TAG" && filters.searchQuery?.trim();
  const isContentTagSearch = filters.searchType === "CONTENT_TAG" && filters.searchQuery?.trim();
  const isAllSearch = filters.searchType === "ALL" && filters.searchQuery?.trim();
  const isTextSearch = filters.searchQuery?.trim() && filters.searchType !== "ALL" && filters.searchType !== "TAG" && filters.searchType !== "CONTENT_TAG";

  // 한국 시간 기준 날짜를 UTC로 변환하는 함수
  const convertDateRangeToUTC = (range: DateRange) => {
    if (!range.startDate && !range.endDate) return undefined;

    const result: { startDate?: string; endDate?: string } = {};

    if (range.startDate) {
      // 선택한 날짜의 전날 15:00:00.000Z (한국 시간 당일 00:00:00)
      result.startDate = `${range.startDate}T15:00:00.000Z`;
      const [year, month, day] = range.startDate.split('-');
      const selectedDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0));
      const prevDay = new Date(selectedDate.getTime() - (24 * 60 * 60 * 1000));
      result.startDate = `${prevDay.getUTCFullYear()}-${String(prevDay.getUTCMonth() + 1).padStart(2, '0')}-${String(prevDay.getUTCDate()).padStart(2, '0')}T15:00:00.000Z`;
    }

    if (range.endDate) {
      // 선택한 날짜 14:59:59.999Z (한국 시간 당일 23:59:59.999)
      result.endDate = `${range.endDate}T14:59:59.999Z`;
    }

    return result;
  };

  // Drafts query - DELETED 상태는 사용자에게 보여지지 않음
  const { data: draftsData, loading: draftsLoading, refetch: refetchDrafts } = useOmniDraftsQuery({
    variables: {
      input: {
        pagination: {
          page: filters.page,
          limit: filters.limit,
        },
        sortBy: filters.sortKey || undefined,
        sortOrder: filters.sortDirection ? (filters.sortDirection === "ASC" ? SortOrder.Asc : SortOrder.Desc) : undefined,
        status: filters.statusFilter ? (filters.statusFilter as DraftStatus) : undefined,
        dateRange: convertDateRangeToUTC(dateRange),
        search: (isTextSearch || isContentTagSearch || isAllSearch || isTagSearch) ? {
          keyword: filters.searchQuery.trim(),
          fields: getSearchFields(filters.searchType)
        } : undefined,
      }
    }
  });

  // Draft detail query (for detail page)
  const { data: draftDetailData, loading: draftDetailLoading, refetch: refetchDraftDetail } = useOmniDraftQuery({
    variables: {
      draftId: draftId || "",
    },
    skip: !draftId, // draftId가 없으면 쿼리 스킵
  });

  // Delete draft mutation
  const [deleteDraft] = useOmniDeleteDraftMutation();

  // API 데이터를 Draft 인터페이스에 맞게 변환
  const posts: Draft[] = (draftsData?.drafts?.items || []).map(item => ({
    draftId: item.draftId,
    title: item.title,
    status: item.status as Draft['status'],
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    originalPostId: item.originalPostId,
    artifactId: item.artifactId,
    channelPostPlans: item.channelPostPlans?.map(plan => ({
      channelRef: {
        name: plan.channelRef.name,
      },
      status: plan.status,
    })) || [],
  }));
  const totalPages = draftsData?.drafts?.totalPages || 0;
  const loading = draftsLoading;

  // Refetch when filters change
  useEffect(() => {
    refetchDrafts();
  }, [filters, dateRange, refetchDrafts]);

  const handleSearch = () => {
    refetchDrafts();
  };

  const handleDatePresetSelect = (preset: string) => {
    setSelectedDatePreset(preset);
    // 한국 시간(KST, UTC+9) 기준으로 오늘 날짜 가져오기
    const getKSTDate = () => {
      const now = new Date();
      // UTC 시간에 9시간을 더해 한국 시간으로 변환
      const kstOffset = 9 * 60; // 9시간을 분으로 변환
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // UTC 시간
      return new Date(utc + (kstOffset * 60000)); // KST 시간
    };
    const today = getKSTDate();
    // 날짜를 YYYY-MM-DD 형식으로 변환 (화면 표시용)
    const getDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (preset) {
      case "ALL":
        setDateRange({});
        break;
      case "TODAY":
        setDateRange({ startDate: getDateString(today), endDate: getDateString(today) });
        break;
      case "3DAYS":
        const threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(today.getDate() - 3);
        setDateRange({ startDate: getDateString(threeDaysAgo), endDate: getDateString(today) });
        break;
      case "7DAYS":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setDateRange({ startDate: getDateString(sevenDaysAgo), endDate: getDateString(today) });
        break;
      case "1MONTH":
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        setDateRange({ startDate: getDateString(oneMonthAgo), endDate: getDateString(today) });
        break;
      case "6MONTHS":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        setDateRange({ startDate: getDateString(sixMonthsAgo), endDate: getDateString(today) });
        break;
      case "1YEAR":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        setDateRange({ startDate: getDateString(oneYearAgo), endDate: getDateString(today) });
        break;
      case "CUSTOM":
        break;
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      // APPROVED 상태인 원고들만 선택
      const approvedDraftIds = posts
        .filter(post => post.status === 'APPROVED')
        .map(post => post.draftId);
      setSelectedIds(approvedDraftIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectPost = (draftId: string, selected: boolean) => {
    if (selected) {
      // APPROVED 상태인 원고만 선택 가능
      const draft = posts.find(post => post.draftId === draftId);
      if (draft && draft.status === 'APPROVED') {
        setSelectedIds(prev => [...prev, draftId]);
      }
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== draftId));
    }
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    
    const confirmMessage = `선택된 ${selectedIds.length}개의 원고를 삭제하시겠습니까?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      // 선택된 draft들을 순차적으로 삭제
      for (const draftId of selectedIds) {
        await deleteDraft({
          variables: {
            input: {
              draftId: draftId,
            }
          }
        });
      }
      
      setSelectedIds([]); // 삭제 완료 후 선택 해제
      alert(`${selectedIds.length}개의 원고가 삭제되었습니다.`);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("원고 삭제에 실패했습니다.");
    }
  };

  const handlePostClick = (post: Draft) => {
    navigate(`/omni/manuscript/${post.draftId}`);
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    onChange("sortKey", key);
    onChange("sortDirection", direction);
  };

  // 초기화 기능
  const handleReset = () => {
    // 모든 필터 초기화
    onChange("searchQuery", "");
    onChange("searchType", "ALL");
    onChange("statusFilter", "");
    onChange("dateTypeFilter", "CREATED");
    onChange("sortKey", "");
    onChange("sortDirection", "ASC");
    onChange("page", 1);
    
    // 날짜 관련 초기화
    setDateRange({});
    setSelectedDatePreset("ALL");
    
    // 선택된 항목 초기화
    setSelectedIds([]);
  };

  // 상세 모달에서 저장/수정/삭제 처리
  const handleDetailSave = (updatedPost: DraftDetail) => {
    // TODO: Implement draft update mutation
    refetchDrafts();
    navigate('/omni/manuscript');
  };


  const handleDetailPageClose = () => {
    // 목록 데이터 갱신 후 목록 페이지로 이동
    refetchDrafts();
    navigate('/omni/manuscript');
  };

  // URL 파라미터로 상세 페이지 판단
  if (draftId) {
    if (draftDetailLoading) {
      return <div>Loading...</div>;
    }
    
    const selectedDraft = draftDetailData?.draft;
    if (selectedDraft) {
      return (
        <PageWrapper className="!p-0 !bg-white !max-h-none !h-auto">
          <DraftDetailPage
            draft={selectedDraft}
            onClose={handleDetailPageClose}
            onSave={handleDetailSave}
            onRefreshDraft={refetchDraftDetail}
            refetchDraft={refetchDraftDetail}
          />
        </PageWrapper>
      );
    }
    // 존재하지 않는 draft ID인 경우 목록으로 리다이렉트
    navigate('/omni/manuscript');
    return null;
  }

  return (
    <div className="w-full min-w-[1200px] max-w-full bg-zinc-100 p-3 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-2xl font-bold">원고관리</h1>
      </div>

      <div className="mb-4 flex-shrink-0">
        <DraftSearchFilters
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => onChange("searchQuery", value)}
          onSearch={handleSearch}
          searchType={filters.searchType}
          onSearchTypeChange={(value) => onChange("searchType", value)}
          statusFilter={filters.statusFilter}
          onStatusChange={(value) => onChange("statusFilter", value)}
          dateTypeFilter={filters.dateTypeFilter}
          onDateTypeChange={(value) => onChange("dateTypeFilter", value)}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onDatePresetSelect={handleDatePresetSelect}
          selectedDatePreset={selectedDatePreset}
          onReset={handleReset}
        />
      </div>

      <div className="flex-1 min-h-0 mb-4">
        <DraftTable
          posts={posts}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectPost={handleSelectPost}
          onPostClick={handlePostClick}
          isLoading={loading}
          sortKey={filters.sortKey}
          sortDirection={filters.sortDirection || undefined}
          onSort={handleSort}
        />
      </div>

      <div className="flex flex-col gap-3 flex-shrink-0" style={{ minHeight: selectedIds.length > 0 ? '80px' : '50px' }}>
        <OmniTable.Pagination
          total={totalPages}
          page={filters.page}
          onPageChange={(page) => onChange("page", page)}
        />

        <DraftBulkActions
          selectedCount={selectedIds.length}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}